import { useLayoutEffect, useSyncExternalStore } from 'react'

/**
 * ADR-106 P3 — the one app-wide answer to "is anything blocking top-level
 * swipe navigation right now?".
 *
 * ADR-106 Decision 3.4 makes blocking the default for every overlay and mode
 * that sits over a chain screen: while one is open, the swipe does not start.
 * The swipe consults this signal rather than knowing about any overlay, so an
 * overlay that joins here is covered and one that does not is a defect
 * (enforced by `test/swipeNavBlockGuard.test.ts`).
 *
 * Built against the leak — a block that stays on after everything has closed,
 * which would silently kill the gesture — rather than for brevity:
 *
 *  - **A set of tokens, not a counter.** Each open surface holds its own
 *    token. A double release cannot drive a count below zero and a missed
 *    release cannot be masked by someone else's; the set is empty exactly
 *    when every holder has let go.
 *  - **Held only by a React effect.** A token is taken in a layout effect and
 *    given back by that effect's cleanup, so it is released by *every* path
 *    that ends the surface: its `active` flag turning false (own close
 *    control, backdrop, Escape, device back), the component unmounting
 *    (navigation away, a parent closing it), or an error boundary tearing the
 *    subtree down. No close handler has to remember to release anything.
 *  - **Idempotent release.** Releasing a token twice is a no-op, which is
 *    what makes StrictMode's mount → unmount → mount double-run safe.
 *
 * A layout effect (not a passive one) so the block is in place in the same
 * commit that shows the surface, before the browser paints it and before any
 * touch on it can be delivered.
 */

let nextToken = 1
const holders = new Map<number, string>()
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

/**
 * Takes a block and returns its release. Prefer `useSwipeNavBlock`; this
 * exists for the hook and for tests.
 */
export function acquireSwipeNavBlock(reason: string): () => void {
  const token = nextToken++
  holders.set(token, reason)
  emit()
  return () => {
    if (holders.delete(token)) emit()
  }
}

/** Whether swipe navigation is blocked right now. Readable from anywhere. */
export function isSwipeNavBlocked(): boolean {
  return holders.size > 0
}

/** What is holding the block, one entry per open surface (for debugging and tests). */
export function getSwipeNavBlockers(): string[] {
  return [...holders.values()]
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** The same signal as a React value that re-renders when it changes. */
export function useSwipeNavBlocked(): boolean {
  return useSyncExternalStore(subscribe, isSwipeNavBlocked, isSwipeNavBlocked)
}

/**
 * Joins the block while `active` is true. Call it in the component that owns
 * the surface, with the surface's own open condition — or `true` in a
 * component that is mounted only while its surface is open.
 */
export function useSwipeNavBlock(active: boolean, reason: string): void {
  useLayoutEffect(() => {
    if (!active) return
    return acquireSwipeNavBlock(reason)
  }, [active, reason])
}
